// ============================================
// Wizard AI Generation
// AI 생성 호출 및 결과 적용
// ============================================

import { authFetch } from '../../shared/auth-fetch.js';
import { CREATOR_ENDPOINTS } from '../../shared/api-endpoints.js';
import { showToast } from '../../utils.js';
import { CURRENCY } from '../../constants.js';
import { showLoadingWithMessages, hideLoadingWithMessages } from '../../shared/autofill-utils.js';
import {
  getWizardState,
  setIsGenerating,
  resetWizardState,
  getSelectedTags
} from './state.js';
import { closeWizardModal, updateNavigationButtons } from './modal.js';
import { applyAutoFilledData } from '../autofill/handler.js';
import { openCharacterEditor } from '../editor/modal.js';
import { saveCreationData } from '../save.js';
import { updateCarbonDisplay } from '../../mypage/carbon.js';

// ============================================
// 메인 생성 함수
// ============================================

export async function generateFromWizard() {
  const state = getWizardState();

  // 이미 생성 중이면 무시
  if (state.isGenerating) {
    return;
  }

  // 이미지 모델 선택 확인
  if (!state.imageModelId) {
    showToast('이미지 옵션을 선택해주세요', 'error');
    return;
  }

  // 'none'이면 이미지 생성 안함
  const needsImage = state.imageModelId !== 'none';

  setIsGenerating(true);
  updateNavigationButtons();

  // 로딩 표시
  showLoadingWithMessages('autofill');

  try {
    const response = await authFetch(CREATOR_ENDPOINTS.autoFillCreation, {
      body: {
        wizardMode: true,
        wizardData: {
          gender: state.gender,
          description: state.description || '',
          tags: getSelectedTags(),
          background: state.introScene || ''  // 점술가 배경
        },
        needsImage: needsImage,
        needsIntro: true,
        imageModelId: needsImage ? state.imageModelId : null,
        // 마법사 모드 비용: 10 카본
        baseCarbonCost: 10,
        // 점술가 모드
        fortuneTellerMode: true
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Rate limit 에러 처리
      if (response.status === 429) {
        showToast(errorData.error?.message || '요청이 너무 많습니다. 잠시 후 다시 시도해주세요', 'error');
        return;
      }

      // 카본 부족 에러 처리
      if (response.status === 402 || errorData.error?.code === 'INSUFFICIENT_CARBON') {
        showToast(`${CURRENCY.NAME}가 부족합니다`, 'error');
        return;
      }

      throw new Error(errorData.error?.message || '생성 중 오류가 발생했습니다');
    }

    const result = await response.json();

    if (result.success && result.data) {
      // 마법사 모달 닫기
      closeWizardModal();

      // 에디터 열기
      await openCharacterEditor(null);

      // 생성된 데이터 적용 (점술가 필드 구조)
      const allFields = ['name', 'experience', 'prompt'];

      // 성별은 사용자가 선택한 값 사용
      result.data.character = result.data.character || {};
      result.data.character.gender = state.gender;

      await applyAutoFilledData(result.data, allFields, true, false);

      // 임시저장
      await saveCreationData('draft', { silent: true, skipClose: true });

      showToast('점술가가 생성되었어요! 확인 후 수정해주세요');

      // 카본 잔액 업데이트
      if (typeof result.carbon === 'number') {
        updateCarbonDisplay(result.carbon);
      }

      // 이미지 환불 알림
      if (result.data.imageRefunded) {
        showToast('이미지 생성에 실패하여 이미지 비용이 환불되었어요', 'warning');
      }

      console.log('[Wizard] 생성 완료', {
        hasImage: !!result.data.imageUrl,
        carbonUsed: result.carbonUsed,
        carbonBalance: result.carbon
      });

    } else {
      throw new Error(result.error?.message || '생성 결과가 없습니다');
    }

  } catch (error) {
    console.error('[Wizard] 생성 오류:', error);
    showToast(error.message || '생성 중 오류가 발생했어요', 'error');
  } finally {
    setIsGenerating(false);
    updateNavigationButtons();
    hideLoadingWithMessages();
    resetWizardState();
  }
}

// ============================================
// 비용 계산
// ============================================

export function calculateWizardCost(state, imageModels) {
  // 기본 비용: 10 카본
  let cost = 10;

  // 이미지 생성 시 모델 비용 추가 ('none'이 아닌 경우)
  if (state.imageModelId && state.imageModelId !== 'none' && imageModels) {
    const model = imageModels.find(m => m.id === state.imageModelId);
    if (model?.carbonCost) {
      cost += model.carbonCost;
    }
  }

  return cost;
}
